import { useEffect, useRef, useState } from "react";
import { Empty, Spin } from "antd";
import dayjs from "dayjs";
import trans from "../../../utils";
import { useGetServiceRequestHistoryQuery } from "../../../Redux/service/Dispatcher/Dispatcher";
import FormSubmissionPopup from "../../Tasks/Popups/ViewTask/FormSubmissionPopup";

const ACTION_TITLES = {
  CREATED: "Request Created",
  UPDATED: "Request Updated",
  ASSIGNED: "Technician Assigned",
  REASSIGNED: "Technician Reassigned",
  TASK_CREATED: "Task Created",
  CANCELLED: "Request Cancelled",
  TASK_STARTED: "Started Visit",
  TASK_STATUS_CHANGED: "Task Status Updated",
  TASK_COMPLETED: "Task Completed",
  FORM_SUBMITTED: "Form Submitted",
  REQUEST_COMPLETED: "Request Completed",
};

// FORM_SUBMITTED entries carry "submissionId:<id>" in notes so the entry can
// link straight to that submission instead of just describing it.
const SUBMISSION_NOTE_PATTERN = /^submissionId:(\d+)$/;

const PAGE_SIZE = 10;

export default function RequestActivityHistory({ requestId }) {
  const [page, setPage] = useState(1);
  const [openSubmissionId, setOpenSubmissionId] = useState(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setPage(1);
  }, [requestId]);

  const { data, isFetching } = useGetServiceRequestHistoryQuery(
    { id: requestId, page, limit: PAGE_SIZE },
    { skip: !requestId, refetchOnMountOrArgChange: true },
  );
  const entries = data?.data || [];
  const total = data?.pagination?.totalItems ?? entries.length;
  const hasMore = entries.length < total;

  useEffect(() => {
    if (!hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetching) {
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isFetching, entries.length]);

  if (isFetching && page === 1) {
    return (
      <div className="loader-container">
        <Spin />
      </div>
    );
  }

  if (entries.length === 0) {
    return <Empty description={trans("No activity yet")} />;
  }

  return (
    <>
      <div>
      {entries.map((entry, index) => (
        <div key={entry.id} className="d-flex" style={{ gap: 12 }}>
          <div className="d-flex flex-column align-items-center">
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: "#2E9245",
                flexShrink: 0,
                marginTop: 4,
              }}
            />
            {index < entries.length - 1 && (
              <span
                style={{ width: 2, flex: 1, background: "#e5e5e5", marginTop: 4 }}
              />
            )}
          </div>
          <div className="flex-grow-1 pb-4 d-flex justify-content-between">
            <div>
              <p className="fw-500 mb-1">
                {trans(ACTION_TITLES[entry.action] || entry.action)}
              </p>
              <p className="neutral-400 mb-1">
                {trans("By")} : {entry.userRole ? trans(entry.userRole) : entry.userName}
              </p>
              <p className="neutral-400 mb-0">
                {entry.previousValue || trans("No Action")} → {entry.newValue}
              </p>
              {entry.notes && SUBMISSION_NOTE_PATTERN.test(entry.notes) ? (
                <p className="mb-0">
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={() =>
                      setOpenSubmissionId(Number(entry.notes.match(SUBMISSION_NOTE_PATTERN)[1]))
                    }
                  >
                    {trans("View Submission")}
                  </button>
                </p>
              ) : (
                entry.notes && <p className="neutral-400 mb-0">{entry.notes}</p>
              )}
            </div>
            <div className="neutral-400 text-nowrap">
              {dayjs(entry.changedAt).format("DD MMM YYYY • hh:mm A")}
            </div>
          </div>
        </div>
      ))}
      {hasMore && (
        <div ref={sentinelRef} className="d-flex justify-content-center py-2">
          <Spin size="small" />
        </div>
      )}
      </div>
      <FormSubmissionPopup formId={openSubmissionId} onClose={() => setOpenSubmissionId(null)} zIndex={1200} />
    </>
  );
}
